import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getPlayersEntries, createAgencyFolder } from "../../services/api";
// The real, working links flow (FolderDetail's successor page uses the same
// component). QAV5-406: this legacy page's own "Add drive link" Save used to
// be a `console.log` stub that discarded the URL silently.
import AddLinkModal from "../../components/agency/AddLinkModal";

const AgencyPlayerEntries = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State for dynamic entries
  const [dynamicEntries, setDynamicEntries] = useState({
    waiting: [],
    accepted: [],
    rejected: [],
  });
  // State for player info (from API)
  const [playerInfo, setPlayerInfo] = useState(null);
  // Loading state
  const [loading, setLoading] = useState(false);
  // Drive-link modal: this page has no folder of its own, only a player id
  // (the same `id` already used above for getPlayersEntries — both that
  // endpoint and createAgencyFolder resolve it against the same Player.id,
  // confirmed against the backend views). The folder is get-or-created
  // (createAgencyFolder is idempotent per (agency, player), see
  // AgencyFolderListCreateAPIView.post) right before the modal opens.
  const [showDriveLinkModal, setShowDriveLinkModal] = useState(false);
  const [driveLinkFolderId, setDriveLinkFolderId] = useState(null);
  const [resolvingFolder, setResolvingFolder] = useState(false);

  const handleOpenDriveLinkModal = async () => {
    setResolvingFolder(true);
    try {
      const res = await createAgencyFolder(id);
      if (res.success) {
        setDriveLinkFolderId(res.data?.id ?? null);
        setShowDriveLinkModal(true);
      } else {
        toast.error(res.error || "Failed to open the links folder");
      }
    } finally {
      setResolvingFolder(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const fetchEntries = async () => {
      try {
        const res = await getPlayersEntries(id);
        if (res.success && res.data?.data?.player && res.data?.data?.entries) {
          setPlayerInfo(res.data.data.player);
          setDynamicEntries({
            waiting: res.data.data.entries.waiting || [],
            accepted: res.data.data.entries.accepted || [],
            rejected: res.data.data.entries.rejected || [],
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchEntries();
  }, [id]);

  const getStatusColor = (status) => {
    switch (status) {
      case "waiting":
        return "border-[#FFD79E]";
      case "accepted":
        return "border-[#71DBEE]";
      case "rejected":
        return "border-[#FF7A93]";
      default:
        return "border-l-gray-400";
    }
  };

  const getClubLogo = (clubName) => {
    const clubLogos = {
      "Real Madrid": "/public/logo512.png",
      Valencia: "/public/logo512.png",
      Girona: "/public/logo512.png",
      Mallorca: "/public/logo512.png",
      Granada: "/public/logo512.png",
      "Chelsea Football Club": "/public/logo512.png",
    };
    return clubLogos[clubName] || "/public/logo512.png";
  };

  // Map API entry to card format
  const mapApiEntryToCard = (entry) => ({
    club: {
      name: entry.club,
      logo: getClubLogo(entry.club),
    },
    notes: entry.notes,
    deviceUsed: entry.mode_of_communication
      ? entry.mode_of_communication.charAt(0).toUpperCase() +
        entry.mode_of_communication.slice(1)
      : "-",
    date: entry.date ? new Date(entry.date).toLocaleDateString("en-GB") : "-",
    id: entry.id,
    agent: entry.agent,
  });

  const EntryCard = ({ entry, status }) => (
    <div
      className={`bg-white rounded-lg shadow-sm border-l-4 ${getStatusColor(
        status
      )} p-4 mb-4`}
    >
      <div className="flex items-center gap-3 mb-3">
        <img
          src={entry.club.logo}
          alt={entry.club.name}
          className="w-8 h-8 rounded-full object-cover"
        />
        <span className="font-medium text-[#222A35] text-[16px]">
          {entry.club.name}
        </span>
      </div>
      <div className="bg-[#F8F9FB] rounded-lg p-3 mb-4">
        <p className="text-[#000000] text-sm leading-relaxed">
          <span className="font-medium text-[#000000B2]">Notes:</span>{" "}
          {entry.notes}
        </p>
      </div>
      <div className="flex items-center justify-between text-sm text-[#242426]">
        <span>
          Device used: <span className="font-semibold">{entry.deviceUsed}</span>
        </span>
        <span>{entry.date}</span>
      </div>
    </div>
  );

  // Compose entries for each status: dynamic (API) only
  const getAllEntries = (status) => {
    const apiEntries = (dynamicEntries[status] || []).map(mapApiEntryToCard);
    return [...apiEntries];
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-2"></div>
      <span className="text-gray-400 text-sm">Loading...</span>
    </div>
  );

  // Empty state component
  const EmptyState = ({ label }) => (
    <div className="flex flex-col items-center justify-center py-12">
      <span className="text-gray-400 text-sm">No {label} entries yet.</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className=" mx-auto  pb-8">
        {/* Header */}
        <div className="flex items-center justify-between py-4 mb-6 px-12 border border-[#E5E6E8]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="text-[#2C2C2C]"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.8327 6.99984H1.16602M1.16602 6.99984L6.99935 12.8332M1.16602 6.99984L6.99935 1.1665"
                  stroke="#2C2C2C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-[#202630]">
              {playerInfo?.name || "Federico Valverde"}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleOpenDriveLinkModal}
              disabled={resolvingFolder}
              className="text-[#1E1E1E] hover:text-gray-900 flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E6E6E6] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                width="16"
                height="10"
                viewBox="0 0 16 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0003 1.66675H12.0003C12.4381 1.66675 12.8715 1.75297 13.2759 1.92048C13.6804 2.088 14.0478 2.33353 14.3573 2.64306C14.6669 2.95259 14.9124 3.32005 15.0799 3.72447C15.2474 4.12889 15.3337 4.56234 15.3337 5.00008C15.3337 5.43782 15.2474 5.87127 15.0799 6.27569C14.9124 6.68011 14.6669 7.04758 14.3573 7.3571C14.0478 7.66663 13.6804 7.91216 13.2759 8.07968C12.8715 8.2472 12.4381 8.33341 12.0003 8.33341H10.0003M6.00033 8.33341H4.00033C3.56259 8.33341 3.12913 8.2472 2.72471 8.07968C2.3203 7.91216 1.95283 7.66663 1.6433 7.3571C1.01818 6.73198 0.666992 5.88414 0.666992 5.00008C0.666992 4.11603 1.01818 3.26818 1.6433 2.64306C2.26842 2.01794 3.11627 1.66675 4.00033 1.66675H6.00033M5.33366 5.00008H10.667"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Add drive link
            </button>
            <button
              onClick={() => {
                if (playerInfo) {
                  navigate("/agency/add-manual-entry", {
                    state: { player: playerInfo },
                  });
                } else {
                  navigate("/agency/add-manual-entry");
                }
              }}
              className="bg-[#2283FF] hover:bg-blue-700 text-[#F5F5F5] px-4 py-2 rounded-lg text-sm inline-flex items-center"
            >
              <span className="mr-1">+</span> Add new entry
            </button>
          </div>
        </div>

        {/* Columns Grid */}
        <div className="grid grid-cols-3 gap-6 px-12 ">
          {/* Waiting Column */}
          <div className="bg-[#F5F5F5] px-6 py-8 rounded-lg">
            <h2 className="font-bold text-[#1A212F] text-xl mb-4">
              Waiting ({getAllEntries("waiting").length})
            </h2>
            <div className="space-y-4 mt-10 min-h-[180px]">
              {loading ? (
                <LoadingSpinner />
              ) : getAllEntries("waiting").length === 0 ? (
                <EmptyState label="waiting" />
              ) : (
                getAllEntries("waiting").map((entry, index) => (
                  <EntryCard
                    key={entry.id || index}
                    entry={entry}
                    status="waiting"
                  />
                ))
              )}
            </div>
          </div>

          {/* Accepted Column */}
          <div className="bg-[#F5F5F5] px-6 py-8 rounded-lg">
            <h2 className="font-bold text-[#1A212F] text-xl mb-4">
              Accepted ({getAllEntries("accepted").length})
            </h2>
            <div className="space-y-4 mt-10 min-h-[180px]">
              {loading ? (
                <LoadingSpinner />
              ) : getAllEntries("accepted").length === 0 ? (
                <EmptyState label="accepted" />
              ) : (
                getAllEntries("accepted").map((entry, index) => (
                  <EntryCard
                    key={entry.id || index}
                    entry={entry}
                    status="accepted"
                  />
                ))
              )}
            </div>
          </div>

          {/* Rejected Column */}
          <div className="bg-[#F5F5F5] px-6 py-8 rounded-lg">
            <h2 className="font-bold text-[#1A212F] text-xl mb-4">
              Rejected ({getAllEntries("rejected").length})
            </h2>
            <div className="space-y-4 mt-10 min-h-[180px]">
              {loading ? (
                <LoadingSpinner />
              ) : getAllEntries("rejected").length === 0 ? (
                <EmptyState label="rejected" />
              ) : (
                getAllEntries("rejected").map((entry, index) => (
                  <EntryCard
                    key={entry.id || index}
                    entry={entry}
                    status="rejected"
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Drive Link Modal — same real component/flow as FolderDetail.js. */}
      <AddLinkModal
        isOpen={showDriveLinkModal}
        onClose={() => setShowDriveLinkModal(false)}
        folderId={driveLinkFolderId}
      />
    </div>
  );
};

export default AgencyPlayerEntries;
